import { Box, Typography, Pagination, ButtonBase, Divider } from '@mui/material';
import type * as React from 'react';

type PaginationControlsProps = {
  displayRowCount: number;
  displayPage: number;
  displayPageSize: number;
  handlePageChange: (event: unknown, newPage: number) => void;
  handlePageSizeChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  showPageSizeSelector?: boolean;
  compact?: boolean;
};

/**
 * Pagination controls component for DataGrid footer
 * Displays page size selector and pagination controls
 */
export const PaginationControls = ({
  displayRowCount,
  displayPage,
  displayPageSize,
  handlePageChange,
  handlePageSizeChange,
  showPageSizeSelector = true,
  compact = false,
}: PaginationControlsProps) => {
  const BASE_PAGE_SIZES = [10, 25, 50, 100];
  const pageSizeOptions = BASE_PAGE_SIZES.includes(displayPageSize)
    ? BASE_PAGE_SIZES
    : [...BASE_PAGE_SIZES, displayPageSize].sort((a, b) => a - b);

  const totalPages = Math.ceil(displayRowCount / displayPageSize);
  const currentPageNumber = displayPage + 1;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'stretch', md: 'center' },
        flexWrap: compact ? 'wrap' : 'nowrap',
        borderTop: '1px solid',
        borderColor: 'divider',
        width: '100%',
        bgcolor: 'background.paper',
        pb: { xs: 'max(12px, env(safe-area-inset-bottom))', md: 0 },
      }}
    >
      {showPageSizeSelector && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: { xs: 'space-between', md: 'flex-start' },
            width: { xs: '100%', md: 'auto' },
            px: compact ? 1 : 2,
            py: compact ? 1 : 1.5,
            gap: 2,
            flex: { md: 1 },
          }}
        >
          <Typography variant={compact ? 'caption' : 'body2'} sx={{ whiteSpace: 'nowrap' }}>
            Rows per page:
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {pageSizeOptions.map((size) => (
              <ButtonBase
                key={size}
                onClick={() =>
                  handlePageSizeChange({
                    target: { value: String(size) },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
                sx={{
                  minWidth: compact ? 32 : 36,
                  height: compact ? 28 : 32,
                  px: 1,
                  borderRadius: 1,
                  fontSize: compact ? '0.75rem' : '0.875rem',
                  fontWeight: displayPageSize === size ? 600 : 400,
                  bgcolor: displayPageSize === size ? 'primary.main' : 'background.paper',
                  color: displayPageSize === size ? 'primary.contrastText' : 'text.primary',
                  border: '1px solid',
                  borderColor: displayPageSize === size ? 'primary.main' : 'divider',
                  '&:hover': {
                    bgcolor: displayPageSize === size ? 'primary.dark' : 'action.hover',
                  },
                  transition: 'all 0.2s',
                }}
              >
                {size}
              </ButtonBase>
            ))}
          </Box>
        </Box>
      )}

      {showPageSizeSelector && (
        <Divider
          sx={{
            display: { xs: 'block', md: 'none' },
            alignSelf: 'stretch',
            width: 'auto',
          }}
        />
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: { xs: 'space-between', md: 'flex-end' },
          width: { xs: '100%', md: 'auto' },
          px: compact ? 1 : 2,
          py: compact ? 1 : 1.5,
          gap: 2,
          flex: { md: 1 },
        }}
      >
        <Typography variant={compact ? 'caption' : 'body2'} sx={{ whiteSpace: 'nowrap' }}>
          Page {currentPageNumber} of {totalPages} ({displayRowCount} items)
        </Typography>
        <Pagination
          count={totalPages}
          page={currentPageNumber}
          onChange={(event, value) => handlePageChange(event, value - 1)}
          showFirstButton={true}
          showLastButton={true}
          siblingCount={compact ? 0 : 1}
          boundaryCount={1}
          size="small"
          sx={{
            '& .MuiPaginationItem-page': {
              display: { xs: 'none', md: 'inline-flex' },
            },
          }}
        />
      </Box>
    </Box>
  );
};
